import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {AuthStateService} from '@auth/states';
import {CompanyStateService} from '@core/states';
import {ApiCollection, ApiResponse, Attachment, SupplierDetail, SupplierNote, SupplierRow} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';

@Injectable({
  providedIn: 'root'
})
export class SupplierService {
  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getSuppliers(page = 1, itemsPerPage = 20, options?: {skipLoader?: boolean}): Promise<ApiResponse<SupplierRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.SUPPLIERS, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const suppliers = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapSupplier(item));
      return {success: true, result: suppliers};
    } catch (error: any) {
      log.error('Error loading suppliers:', error);
    }
    return {success: false, message: 'No se pudieron cargar los proveedores.'};
  }

  async createSupplier(payload: {
    name: string;
    taxId?: string;
    phone?: string;
    email?: string;
    website?: string;
    contact?: string;
    metadata?: Record<string, any>;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<SupplierRow>> {
    try {
      const url = GET_URL(ENDPOINTS.SUPPLIERS);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, payload, headers ? {headers} : undefined);
      return {success: true, result: this.mapSupplier(response)};
    } catch (error: any) {
      log.error('Error creating supplier:', error);
      const message = this.extractValidationMessage(error);
      if (message) {
        return {success: false, message};
      }
    }
    return {success: false, message: 'No se pudo crear el proveedor.'};
  }

  async updateSupplier(supplierId: string | number, payload: {
    name?: string;
    taxId?: string;
    phone?: string;
    email?: string;
    website?: string;
    contact?: string;
    metadata?: Record<string, any>;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<SupplierRow>> {
    try {
      const url = GET_URL(ENDPOINTS.SUPPLIER_DETAIL, {id: supplierId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const response: any = await this.apiService.patch(url, payload, {headers, json: false});
      return {success: true, result: this.mapSupplier(response)};
    } catch (error: any) {
      log.error('Error updating supplier:', error);
      const message = this.extractValidationMessage(error);
      if (message) {
        return {success: false, message};
      }
    }
    return {success: false, message: 'No se pudo actualizar el proveedor.'};
  }

  async getSupplierDetail(supplierId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<SupplierDetail>> {
    try {
      const url = GET_URL(ENDPOINTS.SUPPLIER_DETAIL, {id: supplierId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapSupplierDetail(response)};
    } catch (error: any) {
      log.error('Error loading supplier detail:', error);
    }
    return {success: false, message: 'No se pudo cargar el proveedor.'};
  }

  async getSupplierNotes(supplierId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<SupplierNote[]>> {
    try {
      const url = GET_URL(ENDPOINTS.SUPPLIER_NOTES, {id: supplierId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<SupplierNote> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<SupplierNote>(response).member};
    } catch (error: any) {
      log.error('Error loading supplier notes:', error);
    }
    return {success: false, message: 'No se pudieron cargar las notas.'};
  }

  async addSupplierNote(supplierId: string | number, content: string, options?: {skipLoader?: boolean}): Promise<ApiResponse<SupplierNote>> {
    try {
      const url = GET_URL(ENDPOINTS.SUPPLIER_NOTES, {id: supplierId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: SupplierNote = await this.apiService.post(url, {content}, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error adding supplier note:', error);
    }
    return {success: false, message: 'No se pudo agregar la nota.'};
  }

  async deleteSupplierNote(noteId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.NOTE_DETAIL, {id: noteId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting supplier note:', error);
    }
    return {success: false, message: 'No se pudo eliminar la nota.'};
  }

  async getSupplierAttachments(supplierId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<Attachment[]>> {
    try {
      const url = GET_URL(ENDPOINTS.SUPPLIER_ATTACHMENTS, {id: supplierId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<Attachment> = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.apiService.normalizeCollection<Attachment>(response).member};
    } catch (error: any) {
      log.error('Error loading supplier attachments:', error);
    }
    return {success: false, message: 'No se pudieron cargar los adjuntos.'};
  }

  private mapSupplier(item: any): SupplierRow {
    return {
      id: item.id,
      name: item.name,
      contact: item.contact || item.contactName,
      taxId: item.taxId || item.cif,
      phone: item.phone,
      email: item.email,
      website: item.website || item.web,
      logoUrl: item.logoUrl || item.logo || item.imageUrl || item.image
    };
  }

  private mapSupplierDetail(item: any): SupplierDetail {
    return {
      ...this.mapSupplier(item),
      website: item.website || item.web,
      notesCount: item.notesCount ?? item.notes?.length ?? 0,
      attachmentsCount: item.attachmentsCount ?? item.attachments?.length ?? 0,
      metadata: item.metadata || {}
    };
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }

    return headers;
  }

  private extractValidationMessage(error: any): string {
    if (!error) return '';

    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message.trim();
    }

    const details = error.details;
    if (Array.isArray(details)) {
      const firstViolation = details.find(item => item?.message);
      if (firstViolation?.propertyPath && firstViolation?.message) {
        return `${firstViolation.propertyPath}: ${firstViolation.message}`;
      }
      if (firstViolation?.message) {
        return String(firstViolation.message);
      }
    }

    if (typeof details?.message === 'string' && details.message.trim()) {
      return details.message.trim();
    }

    return '';
  }
}
